import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { usePageLoader } from '@/components/LogoLoader'
import { Select } from '@/components/Select'
import { Dialog, Field, GhostButton, INPUT, PrimaryButton } from '@/features/inventory/shared'
import { fmtDate, useT } from '@/i18n'
import { peopleMessages } from '@/i18n/messages/people'

type PT = ReturnType<typeof usePT>
export function usePT() { return useT(peopleMessages) }
type Key = keyof typeof peopleMessages.en
export const pk = (t: PT, key: string) => t(key as Key)

export const CATEGORIES = ['eu', 'third_country', 'posted', 'posted_third_country'] as const
const WORKER_KINDS = ['residence_permit', 'work_permit', 'a1', 'safety_briefing', 'first_aid', 'machine', 'other'] as const
const SUB_KINDS = ['tax_exemption', 'soka_bau', 'bg', 'liability_insurance', 'trade_registration', 'milog_declaration', 'prequalification', 'other'] as const
const STATUSES = ['blocked', 'not_checked', 'briefing_due', 'expiring', 'cleared'] as const
export type PeopleStatus = typeof STATUSES[number]

interface Worker { employeeId: string; name: string | null; companyId: string | null; company: string | null; isSub: boolean; crewId: string | null; crew: string | null
  lastDay: string | null; category: string | null; status: PeopleStatus; missing: string[]; nextExpiry: string | null; firstAider: boolean }
interface WorkerDoc { id: string; kind: string; title: string | null; issuer: string | null; validFrom: string | null; validTo: string | null; copyOnFile: boolean
  verifiedOn: string; note: string | null; byName: string | null; valid: boolean }
interface WorkerDetail { worker: Worker; profileNote: string | null; documents: WorkerDoc[]; auBriefings: { name: string; signedOn: string }[] }
interface Crew { crewId: string; crew: string | null; company: string | null; members: number; firstAiders: number; notCleared: number }
interface Sub { companyId: string; name: string | null; activeWorkers: number; status: 'blocked' | 'expiring' | 'cleared'; missing: string[]; nextExpiry: string | null; withholdTax: boolean }
interface SubDoc { id: string; kind: string; issuer: string | null; reference: string | null; validFrom: string | null; validTo: string | null; note: string | null; byName: string | null; valid: boolean }

const PILL = 'inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold'
export const STATUS_TONE: Record<string, string> = {
  blocked: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300',
  not_checked: 'bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200',
  briefing_due: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',
  expiring: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',
  cleared: 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300',
}
export const StatusPill = ({ t, status }: { t: PT; status: string }) => <span className={`${PILL} ${STATUS_TONE[status]}`}>{pk(t, `st_${status}`)}</span>
const err = (t: PT, e: unknown) => (e instanceof ApiError ? e.message : t('couldNotSave'))
const Err = ({ t, e }: { t: PT; e: unknown }) => (e ? <p role="alert" className="text-sm" style={{ color: 'var(--viz-critical)' }}>{err(t, e)}</p> : null)
const TABLE = 'w-full text-sm [&_td]:px-3 [&_td]:py-2 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:text-xs [&_th]:font-medium [&_th]:text-neutral-500'

/**
 * People compliance (docs/people-compliance-design.md): every worker checked in over the last 90
 * days with what the law requires of them and whether they hold it; crews and their first-aiders;
 * subcontractors and their certificates. Validity only; the papers stay in the personnel file.
 */
export function PeoplePage() {
  const t = usePT()
  const [tab, setTab] = useState<'workers' | 'crews' | 'subs'>('workers')
  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">{t('title')}</h1>
        <p className="mt-1 max-w-3xl text-sm text-neutral-500">{t('subtitle')}</p>
      </header>
      <div role="tablist" className="flex gap-1 border-b border-neutral-200 dark:border-neutral-800">
        {(['workers', 'crews', 'subs'] as const).map((x) => (
          <button key={x} role="tab" type="button" aria-selected={tab === x} onClick={() => setTab(x)}
            className={`-mb-px min-h-10 border-b-2 px-3 text-sm ${tab === x ? 'border-brand-600 font-semibold text-brand-700 dark:text-brand-300' : 'border-transparent text-neutral-600 dark:text-neutral-300'}`}>
            {pk(t, `tab${x[0]!.toUpperCase()}${x.slice(1)}`)}
          </button>
        ))}
      </div>
      {tab === 'workers' && <Workers t={t} />}
      {tab === 'crews' && <Crews t={t} />}
      {tab === 'subs' && <Subs t={t} />}
      <p className="text-xs text-neutral-500">{t('privacy')}</p>
    </div>
  )
}

function Workers({ t }: { t: PT }) {
  const qc = useQueryClient()
  const { data, isPending } = useQuery<Worker[]>({ queryKey: ['people'], queryFn: () => api.get<Worker[]>('/api/people') })
  const [q, setQ] = useState('')
  const [status, setStatus] = useState<string>('')
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [bulk, setBulk] = useState<string>('third_country')
  const [open, setOpen] = useState<string | null>(null)
  const rows = useMemo(() => {
    const n = q.trim().toLowerCase()
    return (data ?? []).filter((w) => (!status || w.status === status) && (!n || [w.name, w.company, w.crew].some((x) => x?.toLowerCase().includes(n))))
  }, [data, q, status])
  const counts = useMemo(() => Object.fromEntries(STATUSES.map((s) => [s, (data ?? []).filter((w) => w.status === s).length])), [data])
  const apply = useMutation({
    mutationFn: () => api.post('/api/people/profiles', { employeeIds: [...picked], category: bulk }),
    onSuccess: () => { setPicked(new Set()); void qc.invalidateQueries({ queryKey: ['people'] }) },
  })
  const loader = usePageLoader(isPending, t('loading'))
  if (loader) return loader
  const allPicked = rows.length > 0 && rows.every((w) => picked.has(w.employeeId))
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button key={s} type="button" onClick={() => setStatus(status === s ? '' : s)}
            className={`rounded-xl px-3 py-2 text-left ring-1 ${status === s ? 'ring-2 ring-brand-600' : 'ring-neutral-200 dark:ring-neutral-800'} bg-white dark:bg-neutral-900`}>
            <div className="tabular text-xl font-semibold">{counts[s]}</div><StatusPill t={t} status={s} />
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input type="search" id="pp-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('search')} aria-label={t('search')} className={`${INPUT} sm:max-w-xs`} />
        <span className="text-xs text-neutral-500">{t('shown', { shown: rows.length, total: data?.length ?? 0 })}</span>
        {picked.size > 0 && (
          <span className="ml-auto flex flex-wrap items-center gap-2 rounded-xl bg-brand-50 px-3 py-1.5 text-sm dark:bg-brand-500/10">
            {t('selected', { n: picked.size })} · {t('setCategory')}
            <Select id="pp-bulk" className="min-w-[10rem]" value={bulk} onChange={setBulk}
              options={CATEGORIES.map((c) => ({ value: c, label: pk(t, `cat_${c}`) }))} />
            <PrimaryButton onClick={() => apply.mutate()} disabled={apply.isPending} className="min-h-9">{t('apply')}</PrimaryButton>
          </span>
        )}
      </div>
      <Err t={t} e={apply.error} />
      <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
        <table className={TABLE}>
          <thead><tr className="border-b border-neutral-200 dark:border-neutral-800">
            <th><input type="checkbox" aria-label={t('selected', { n: rows.length })} checked={allPicked}
              onChange={() => setPicked(allPicked ? new Set() : new Set(rows.map((w) => w.employeeId)))} /></th>
            <th>{t('colName')}</th><th>{t('colEmployer')}</th><th>{t('colCrew')}</th><th>{t('colLastDay')}</th><th>{t('colCategory')}</th><th>{t('colStatus')}</th><th>{t('colNext')}</th>
          </tr></thead>
          <tbody>
            {rows.map((w) => (
              <tr key={w.employeeId} className="cursor-pointer border-b border-neutral-100 last:border-0 hover:bg-neutral-50 dark:border-neutral-800/60 dark:hover:bg-neutral-800/40" onClick={() => setOpen(w.employeeId)}>
                <td onClick={(e) => e.stopPropagation()}><input type="checkbox" aria-label={w.name ?? ''} checked={picked.has(w.employeeId)}
                  onChange={() => setPicked((p) => { const n = new Set(p); n.has(w.employeeId) ? n.delete(w.employeeId) : n.add(w.employeeId); return n })} /></td>
                <td className="font-medium">{w.name ?? '—'}{w.firstAider && <span className="ml-1.5 text-[11px] text-green-700 dark:text-green-300">+ {t('firstAider')}</span>}</td>
                <td className="text-xs">{w.company ?? '—'}</td>
                <td className="text-xs">{w.crew ?? '—'}</td>
                <td className="tabular text-xs">{fmtDate(w.lastDay)}</td>
                <td className="text-xs">{w.category ? pk(t, `cat_${w.category}`) : <span className="text-neutral-400">{t('catNone')}</span>}</td>
                <td><StatusPill t={t} status={w.status} />{w.missing.length > 0 && <span className="block text-[11px] text-neutral-500">{w.missing.map((m) => pk(t, `wk_${m}`)).join(', ')}</span>}</td>
                <td className="tabular text-xs">{fmtDate(w.nextExpiry)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {open && <WorkerDialog t={t} id={open} onClose={() => setOpen(null)} />}
    </section>
  )
}

function WorkerDialog({ t, id, onClose }: { t: PT; id: string; onClose: () => void }) {
  const qc = useQueryClient()
  const { data } = useQuery<WorkerDetail>({ queryKey: ['person', id], queryFn: () => api.get<WorkerDetail>(`/api/people/${id}`) })
  const [category, setCategory] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [doc, setDoc] = useState<WorkerDoc | 'new' | null>(null)
  const refresh = () => { for (const k of [['person', id], ['people'], ['people-alerts']]) void qc.invalidateQueries({ queryKey: k }) }
  const saveProfile = useMutation({
    mutationFn: () => api.put(`/api/people/${id}/profile`, { category: (category ?? data?.worker.category) || null, note: note ?? data?.profileNote ?? null }),
    onSuccess: refresh,
  })
  if (!data) return null
  const w = data.worker
  return (
    <Dialog title={w.name ?? '—'} hint={[w.company, w.crew].filter(Boolean).join(' · ')} onClose={onClose}
      footer={<><GhostButton onClick={onClose}>{t('cancel')}</GhostButton><PrimaryButton onClick={() => saveProfile.mutate()} disabled={saveProfile.isPending}>{saveProfile.isPending ? t('saving') : t('save')}</PrimaryButton></>}>
      <div className="flex flex-wrap items-center gap-2"><StatusPill t={t} status={w.status} />{w.missing.length > 0 && <span className="text-xs text-neutral-500">{t('missing', { list: w.missing.map((m) => pk(t, `wk_${m}`)).join(', ') })}</span>}</div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('category')}>
          <Select id="pp-cat" value={category ?? w.category ?? ''} onChange={setCategory}
            empty={{ value: '', label: t('catNone') }}
            options={CATEGORIES.map((c) => ({ value: c, label: pk(t, `cat_${c}`) }))} />
        </Field>
        <Field label={t('note')}><input id="pp-note" className={INPUT} value={note ?? data.profileNote ?? ''} onChange={(e) => setNote(e.target.value)} maxLength={500} /></Field>
      </div>
      <Err t={t} e={saveProfile.error} />
      <div className="space-y-2">
        <div className="flex items-center justify-between"><span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{t('papers')}</span>
          <button type="button" onClick={() => setDoc('new')} className="text-xs font-semibold text-brand-700 hover:underline dark:text-brand-300">+ {t('add')}</button></div>
        {data.documents.length === 0 ? <p className="text-sm text-neutral-500">{t('noPapers')}</p> : (
          <ul className="divide-y divide-neutral-100 rounded-xl ring-1 ring-neutral-200 dark:divide-neutral-800 dark:ring-neutral-800">
            {data.documents.map((d) => (
              <li key={d.id} className="flex items-start justify-between gap-2 px-3 py-2 text-sm">
                <span><b>{pk(t, `wk_${d.kind}`)}</b>{d.title && ` · ${d.title}`}
                  <span className="block text-xs text-neutral-500">{[d.issuer, `${fmtDate(d.validFrom)} – ${d.validTo ? fmtDate(d.validTo) : t('openEnded')}`, d.copyOnFile ? t('copyOnFile') : null].filter(Boolean).join(' · ')}</span></span>
                <span className="flex items-center gap-2"><span className={`${PILL} ${d.valid ? STATUS_TONE.cleared : STATUS_TONE.blocked}`}>{d.valid ? t('valid') : t('expired')}</span>
                  <button type="button" onClick={() => setDoc(d)} className="text-xs font-semibold text-brand-700 dark:text-brand-300">{t('edit')}</button></span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="space-y-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{t('auBriefings')}</span>
        {data.auBriefings.length === 0 ? <p className="text-sm text-neutral-500">{t('noAuBriefings')}</p>
          : <ul className="text-sm">{data.auBriefings.map((b, i) => <li key={i}>{b.name} <span className="tabular text-xs text-neutral-500">· {fmtDate(b.signedOn)}</span></li>)}</ul>}
      </div>
      {doc && <DocDialog t={t} kinds={WORKER_KINDS} prefix="wk_" url={`/api/people/${id}/documents`} doc={doc === 'new' ? undefined : doc} worker onClose={() => setDoc(null)} onSaved={refresh} />}
    </Dialog>
  )
}

/** Adds or edits one paper of a worker or a subcontractor. */
function DocDialog({ t, kinds, prefix, url, doc, worker = false, onClose, onSaved }:
  { t: PT; kinds: readonly string[]; prefix: string; url: string; doc?: (WorkerDoc | SubDoc) | undefined; worker?: boolean; onClose: () => void; onSaved: () => void }) {
  const w = doc as WorkerDoc | undefined
  const s = doc as SubDoc | undefined
  const [f, setF] = useState({
    kind: doc?.kind ?? kinds[0]!, title: w?.title ?? '', issuer: doc?.issuer ?? '', reference: s?.reference ?? '', validFrom: doc?.validFrom ?? '',
    validTo: doc?.validTo ?? '', copyOnFile: w?.copyOnFile ?? false, note: doc?.note ?? '',
  })
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }))
  const body = worker
    ? { kind: f.kind, title: f.title || null, issuer: f.issuer || null, validFrom: f.validFrom || null, validTo: f.validTo || null, copyOnFile: f.copyOnFile, verifiedOn: null, note: f.note || null }
    : { kind: f.kind, issuer: f.issuer || null, reference: f.reference || null, validFrom: f.validFrom || null, validTo: f.validTo || null, note: f.note || null }
  const done = () => { onSaved(); onClose() }
  const save = useMutation({ mutationFn: () => (doc ? api.put(`${url}/${doc.id}`, body) : api.post(url, body)), onSuccess: done })
  const del = useMutation({ mutationFn: () => api.del(`${url}/${doc!.id}`), onSuccess: done })
  return (
    <Dialog title={doc ? t('edit') : t('add')} onClose={onClose}
      footer={<>{doc && <GhostButton onClick={() => del.mutate()} className="mr-auto text-red-700">{t('remove')}</GhostButton>}
        <GhostButton onClick={onClose}>{t('cancel')}</GhostButton><PrimaryButton onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? t('saving') : t('save')}</PrimaryButton></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('kind')}><Select id="pp-dk" value={f.kind} onChange={(v) => set('kind')({ target: { value: v } })}
          options={kinds.map((k) => ({ value: k, label: pk(t, `${prefix}${k}`) }))} /></Field>
        {worker ? <Field label={t('titleField')}><input id="pp-dt" className={INPUT} value={f.title} onChange={set('title')} maxLength={120} /></Field>
          : <Field label={t('reference')}><input id="pp-dr" className={INPUT} value={f.reference} onChange={set('reference')} maxLength={120} /></Field>}
        <div className="sm:col-span-2"><Field label={t('issuer')}><input id="pp-di" className={INPUT} value={f.issuer} onChange={set('issuer')} maxLength={200} /></Field></div>
        <Field label={t('validFrom')}><input id="pp-df" type="date" className={INPUT} value={f.validFrom} onChange={set('validFrom')} /></Field>
        <Field label={t('validTo')}><input id="pp-dv" type="date" className={INPUT} value={f.validTo} onChange={set('validTo')} /></Field>
        {worker && <label className="flex items-center gap-2 text-sm sm:col-span-2"><input id="pp-dc" type="checkbox" checked={f.copyOnFile} onChange={(e) => setF((x) => ({ ...x, copyOnFile: e.target.checked }))} />{t('copyOnFile')}</label>}
        <div className="sm:col-span-2"><Field label={t('note')}><input id="pp-dn" className={INPUT} value={f.note} onChange={set('note')} maxLength={500} /></Field></div>
      </div>
      <Err t={t} e={save.error ?? del.error} />
    </Dialog>
  )
}

function Crews({ t }: { t: PT }) {
  const { data } = useQuery<Crew[]>({ queryKey: ['people-crews'], queryFn: () => api.get<Crew[]>('/api/people/crews') })
  if (!data) return null
  return (
    <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
      <table className={TABLE}>
        <thead><tr className="border-b border-neutral-200 dark:border-neutral-800"><th>{t('colCrew')}</th><th>{t('colEmployer')}</th><th>{t('colMembers')}</th><th>{t('colFirstAiders')}</th><th>{t('colNotCleared')}</th></tr></thead>
        <tbody>{data.map((c) => (
          <tr key={c.crewId} className="border-b border-neutral-100 last:border-0 dark:border-neutral-800/60">
            <td className="font-medium">{c.crew ?? '—'}</td><td className="text-xs">{c.company ?? '—'}</td><td className="tabular">{c.members}</td>
            <td>{c.firstAiders > 0 ? <span className="tabular">{c.firstAiders}</span> : <span className={`${PILL} ${STATUS_TONE.blocked}`}>{t('noFirstAider')}</span>}</td>
            <td className={`tabular ${c.notCleared > 0 ? 'font-semibold text-red-700 dark:text-red-300' : ''}`}>{c.notCleared}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  )
}

function Subs({ t }: { t: PT }) {
  const { data } = useQuery<Sub[]>({ queryKey: ['people-subs'], queryFn: () => api.get<Sub[]>('/api/people/subs') })
  const [open, setOpen] = useState<string | null>(null)
  if (!data) return null
  return (
    <>
      <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
        <table className={TABLE}>
          <thead><tr className="border-b border-neutral-200 dark:border-neutral-800"><th>{t('tabSubs')}</th><th>{t('colActive')}</th><th>{t('colStatus')}</th><th>{t('colMissing')}</th><th>{t('colNext')}</th></tr></thead>
          <tbody>{data.map((s) => (
            <tr key={s.companyId} className="cursor-pointer border-b border-neutral-100 last:border-0 hover:bg-neutral-50 dark:border-neutral-800/60 dark:hover:bg-neutral-800/40" onClick={() => setOpen(s.companyId)}>
              <td className="font-medium">{s.name ?? '—'}{s.withholdTax && <span className={`ml-2 ${PILL} ${STATUS_TONE.blocked}`}>{t('withhold')}</span>}</td>
              <td className="tabular">{s.activeWorkers}</td><td><StatusPill t={t} status={s.status} /></td>
              <td className="text-xs">{s.missing.map((m) => pk(t, `sk_${m}`)).join(', ')}</td><td className="tabular text-xs">{fmtDate(s.nextExpiry)}</td>
            </tr>))}
          </tbody>
        </table>
      </div>
      {open && <SubDialog t={t} id={open} onClose={() => setOpen(null)} />}
    </>
  )
}

function SubDialog({ t, id, onClose }: { t: PT; id: string; onClose: () => void }) {
  const qc = useQueryClient()
  const { data } = useQuery<{ sub: Sub; documents: SubDoc[] }>({ queryKey: ['person-sub', id], queryFn: () => api.get(`/api/people/subs/${id}`) })
  const [doc, setDoc] = useState<SubDoc | 'new' | null>(null)
  const refresh = () => { for (const k of [['person-sub', id], ['people-subs'], ['people-alerts']]) void qc.invalidateQueries({ queryKey: k }) }
  if (!data) return null
  return (
    <Dialog title={data.sub.name ?? '—'} onClose={onClose} footer={<GhostButton onClick={onClose}>{t('cancel')}</GhostButton>}>
      <div className="flex flex-wrap items-center gap-2"><StatusPill t={t} status={data.sub.status} />{data.sub.withholdTax && <span className={`${PILL} ${STATUS_TONE.blocked}`}>{t('withhold')}</span>}</div>
      <div className="flex items-center justify-between"><span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{t('papers')}</span>
        <button type="button" onClick={() => setDoc('new')} className="text-xs font-semibold text-brand-700 hover:underline dark:text-brand-300">+ {t('add')}</button></div>
      {data.documents.length === 0 ? <p className="text-sm text-neutral-500">{t('noPapers')}</p> : (
        <ul className="divide-y divide-neutral-100 rounded-xl ring-1 ring-neutral-200 dark:divide-neutral-800 dark:ring-neutral-800">
          {data.documents.map((d) => (
            <li key={d.id} className="flex items-start justify-between gap-2 px-3 py-2 text-sm">
              <span><b>{pk(t, `sk_${d.kind}`)}</b>{d.reference && ` · ${d.reference}`}
                <span className="block text-xs text-neutral-500">{[d.issuer, `${fmtDate(d.validFrom)} – ${d.validTo ? fmtDate(d.validTo) : t('openEnded')}`].filter(Boolean).join(' · ')}</span></span>
              <span className="flex items-center gap-2"><span className={`${PILL} ${d.valid ? STATUS_TONE.cleared : STATUS_TONE.blocked}`}>{d.valid ? t('valid') : t('expired')}</span>
                <button type="button" onClick={() => setDoc(d)} className="text-xs font-semibold text-brand-700 dark:text-brand-300">{t('edit')}</button></span>
            </li>
          ))}
        </ul>
      )}
      {doc && <DocDialog t={t} kinds={SUB_KINDS} prefix="sk_" url={`/api/people/subs/${id}/documents`} doc={doc === 'new' ? undefined : doc} onClose={() => setDoc(null)} onSaved={refresh} />}
    </Dialog>
  )
}
